import { entityTypeNames, type Entity, type EntityType } from '@tunasong/schemas'
import type { TunaPlugin } from '../plugin-types.js'
import type { PluginCategory } from '../plugin-categories.js'
import { usePlugins } from './editor-plugins.js'

interface SortedPlugins {
  plugins: TunaPlugin[]
  allowedTypes: readonly EntityType[]
  category?: PluginCategory
}

/** @todo the plugin should specify whether they are top-level or not */
export const getCreateEntityPlugins = ({ plugins, category, allowedTypes }: SortedPlugins) =>
  plugins
    ?.filter(p => p.node?.type && (allowedTypes as readonly string[]).includes(p.node?.type))
    .filter(p => (category ? (p.category ?? 'other') === category : true))
    /**
     * We require templates in order to create entities. A plugin that declares a node type but no
     * templates is not creatable here — to opt out deliberately, set `disallowUserCreate` instead,
     * which is also what `TemplatePlugins` filters on. @see TunaPlugin.templates
     */
    .filter(p => Boolean(Object.keys(p.templates ?? {}).length > 0))
    // .filter(p => p.icon && p.type && isTopLevelEntityType(p.type))
    .sort((a, b) => {
      if (a.node?.type && b.node?.type && a.node?.type > b.node?.type) {
        return 1
      }
      return -1
    }) as TunaPlugin[]

export const getAllowedChildEntityTypes = ({ parent, plugins }: { parent: Entity; plugins: TunaPlugin[] }) => {
  const parentPlugin = plugins.find(p => p.node?.type === parent?.type)
  if (!parentPlugin?.allowChildrenTypes || parentPlugin?.allowChildrenTypes === true) {
    return entityTypeNames
  }
  return []
}

/** The allowed child types for the parent entity */
export const useAllowedChildEntityTypes = (parent?: Entity, plugins?: TunaPlugin[]) => {
  const allPlugins = usePlugins('all')
  const activePlugins = plugins ?? allPlugins ?? []
  const allowedTypes =
    parent && activePlugins ? getAllowedChildEntityTypes({ parent, plugins: activePlugins }) : ([] as string[])

  return (
    activePlugins
      ?.filter(p => p.node?.type && (allowedTypes as readonly string[]).includes(p.node?.type))
      .map(p => ({
        type: p.node?.type as EntityType,
        description: p.description,
        icon: p.icon,
        color: p.color,
        isPage: p.isPage,
      })) ?? []
  )
}
